export type ReportFormatId =
  | "pqc_inventory"
  | "soc2"
  | "iso27001"
  | "nist_csf"
  | "pci_dss"
  | "hipaa"
  | "cmmc"
  | "cis_controls"
  | "iec_62443"
  | "nerc_cip"
  | "nis2";

export interface ReportCatalogItem {
  id: ReportFormatId;
  group: ReportGroup;
  name: string;
  edition: string;
  focus: string;
}

export type ReportGroup = "General security" | "Industry-specific" | "Regional";

export const REPORT_GROUPS: readonly ReportGroup[] = [
  "General security",
  "Industry-specific",
  "Regional",
];

/**
 * Visual catalog matching the gold reports desk. None of these tiles are a
 * controls engine or a certification. Generating a folio restates admitted
 * scan evidence and names the framework an assessor would map it against.
 */
export const REPORT_CATALOG: readonly ReportCatalogItem[] = [
  {
    id: "pqc_inventory",
    group: "General security",
    name: "PQC inventory",
    edition: "Post-quantum cryptography",
    focus: "Observed crypto uses from this scan. Not a quantum-safe determination.",
  },
  {
    id: "soc2",
    group: "General security",
    name: "SOC 2",
    edition: "AICPA TSC 2017 / 2022 points of focus",
    focus: "Security, availability, confidentiality evidence mapping.",
  },
  {
    id: "iso27001",
    group: "General security",
    name: "ISO 27001",
    edition: "ISO/IEC 27001:2022",
    focus: "ISMS and Annex A evidence mapping.",
  },
  {
    id: "nist_csf",
    group: "General security",
    name: "NIST CSF",
    edition: "NIST CSF 2.0",
    focus: "Govern, identify, protect, detect, respond, recover outcomes.",
  },
  {
    id: "pci_dss",
    group: "General security",
    name: "PCI DSS",
    edition: "PCI DSS 4.0.1",
    focus: "Cardholder-data environment evidence mapping.",
  },
  {
    id: "hipaa",
    group: "General security",
    name: "HIPAA",
    edition: "Security Rule",
    focus: "Administrative, physical, and technical safeguard evidence.",
  },
  {
    id: "cmmc",
    group: "General security",
    name: "CMMC",
    edition: "CMMC 2.0 Levels 1–2",
    focus: "Contractor safeguarding evidence mapping.",
  },
  {
    id: "cis_controls",
    group: "General security",
    name: "CIS Controls",
    edition: "CIS Controls v8.1",
    focus: "Prioritized safeguard evidence mapping.",
  },
  {
    id: "iec_62443",
    group: "Industry-specific",
    name: "IEC 62443",
    edition: "Industrial cybersecurity series",
    focus: "IACS zone and conduit evidence mapping.",
  },
  {
    id: "nerc_cip",
    group: "Industry-specific",
    name: "NERC CIP",
    edition: "CIP reliability standards",
    focus: "BES cyber-system evidence mapping.",
  },
  {
    id: "nis2",
    group: "Regional",
    name: "NIS2",
    edition: "Directive (EU) 2022/2555",
    focus: "Cyber-risk and incident-reporting evidence mapping.",
  },
];

export function catalogItem(id: ReportFormatId): ReportCatalogItem {
  const fallback = REPORT_CATALOG[0];
  if (!fallback) throw new Error("Report catalog must include at least one item");
  return REPORT_CATALOG.find((item) => item.id === id) ?? fallback;
}

export const CERTIFICATION_STATUS = "not-assessed-not-certified" as const;
